import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    TransportAssignmentBulkUpdatePreviewErrorCode,
    TransportAssignmentBulkUpdatePreviewResult,
} from '../transportAssignment.types';
import { TransportOrderBulkUpdateFormStep } from './TransportOrderBulkUpdateFormStep';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import {
    getTransportAssignmentBulkSelection,
    transportAssignmentsSlice,
} from '../../reducers/transportAssignmentsSlice';
import { BulkUpdateValidationFeedback } from '../../sharedComponents/bulkStatusUpdate/BulkUpdateValidationFeedback';
import { BulkStatusError } from '../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';
import { BulkUpdateMaxSizeErrorMessage } from '../../sharedComponents/bulkStatusUpdate/BulkUpdateMaxSizeErrorMessage';
import { MAX_BULK_SELECTION_SIZE } from './TransportOrderBulkUpdate';

type TransportOrderBulkUpdateDialogProps = {
    readonly show: boolean;
    readonly previewResult: TransportAssignmentBulkUpdatePreviewResult;
    readonly onClose: () => void;
};

enum TransportOrderBulkUpdateDialogStep {
    VALIDATION = 'validation',
    FORM = 'form',
}

// eslint-disable-next-line max-lines-per-function
export const TransportOrderBulkUpdateDialog = ({
    show,
    previewResult,
    onClose,
}: TransportOrderBulkUpdateDialogProps) => {
    const dispatch = useAppDispatch();
    const selectedIds = useAppSelector(getTransportAssignmentBulkSelection);
    const [currentStep, setCurrentStep] = useState(TransportOrderBulkUpdateDialogStep.VALIDATION);
    const isValidationStep = currentStep === TransportOrderBulkUpdateDialogStep.VALIDATION;
    const isSuccess = previewResult.success;
    const validOrdersCount = isSuccess ? previewResult.assignments.length : 0;
    const invalidOrders: BulkStatusError<TransportAssignmentBulkUpdatePreviewErrorCode>[] = isSuccess
        ? previewResult.invalidAssignments.map((error) => ({
              errorCode: error.errorCode,
              identifiers: error.assignments,
              identifierType: 'ORDER',
          }))
        : [];
    const nextButtonDisabled = !isSuccess || previewResult.assignments.length === 0;

    const bulkUpdateError =
        !isSuccess && previewResult.errorCode === 'size-exceeded' ? (
            <BulkUpdateMaxSizeErrorMessage
                identifierType="ORDER"
                maxBulkSize={MAX_BULK_SELECTION_SIZE}
                totalCount={selectedIds.length}
            />
        ) : undefined;

    const duplicateVinError =
        !isSuccess && previewResult.errorCode === 'duplicate-vin' ? (
            <div>
                <FormattedMessage id="outboundOrderBook.transportAssignments.bulkUpdate.validationFeedback.failure.duplicateVin" />
            </div>
        ) : undefined;

    const handleOnClose = () => {
        setCurrentStep(TransportOrderBulkUpdateDialogStep.VALIDATION);
        onClose();
    };

    const handleOnDone = () => {
        dispatch(transportAssignmentsSlice.actions.clearBulkSelection());
        handleOnClose();
    };

    return (
        <Dialog show={show} onClose={handleOnClose} showCloseButton bsSize={Dialog.SIZE_LG}>
            <Dialog.Title title={<FormattedMessage id="outboundOrderBook.common.bulkUpdate.dialogTitle" />} />
            <Dialog.Body>
                {isValidationStep ? (
                    <BulkUpdateValidationFeedback
                        validItemsCount={validOrdersCount}
                        invalidItems={invalidOrders}
                        requestError={bulkUpdateError || duplicateVinError}
                    />
                ) : (
                    previewResult.success && (
                        <TransportOrderBulkUpdateFormStep validAssignments={previewResult.assignments} />
                    )
                )}
            </Dialog.Body>
            <Dialog.Footer>
                <ButtonToolbar>
                    {isValidationStep ? (
                        <>
                            <Button onClick={handleOnClose}>
                                <FormattedMessage id="outboundOrderBook.common.button.cancel" />
                            </Button>
                            <Button
                                bsStyle={Button.PRIMARY}
                                onClick={() => setCurrentStep(TransportOrderBulkUpdateDialogStep.FORM)}
                                disabled={nextButtonDisabled}
                            >
                                <FormattedMessage id="outboundOrderBook.common.button.next" />
                            </Button>
                        </>
                    ) : (
                        <Button iconName="rioglyph rioglyph-ok" onClick={handleOnDone}>
                            <FormattedMessage id="outboundOrderBook.common.button.done" />
                        </Button>
                    )}
                </ButtonToolbar>
            </Dialog.Footer>
        </Dialog>
    );
};
